'use client';

/**
 * useVisitorContext — reads the `scw_vctx` cookie from the browser and
 * returns a parsed VisitorContext (or null during SSR / before the
 * bootstrap has run).
 *
 * The initial state is populated synchronously from `document.cookie` so
 * returning visitors get personalized content on first render (no flicker).
 * An event listener refreshes state when the bootstrap writes the cookie
 * for first-time visitors.
 */
import { useEffect, useState } from 'react';
import { VISITOR_CONTEXT_COOKIE, parseVisitorContextCookie, readClientCookie, type VisitorContext } from '@/lib/visitor-context';
export const VISITOR_CONTEXT_UPDATED_EVENT = 'scw:visitor-context-updated';
function readFromCookie(): VisitorContext | null {
  return parseVisitorContextCookie(readClientCookie(VISITOR_CONTEXT_COOKIE));
}
export function useVisitorContext(): VisitorContext | null {
  const [ctx, setCtx] = useState<VisitorContext | null>(() => typeof document === 'undefined' ? null : readFromCookie());
  useEffect(() => {
    // Catch the case where the cookie is written after initial mount
    // (e.g. first visit, bootstrap finished after render).
    const current = readFromCookie();
    if (current && !ctx) setCtx(current);
    const onUpdate = (): void => {
      setCtx(readFromCookie());
    };
    window.addEventListener(VISITOR_CONTEXT_UPDATED_EVENT, onUpdate);
    return () => window.removeEventListener(VISITOR_CONTEXT_UPDATED_EVENT, onUpdate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return ctx;
}